import { useSessionStorage } from '@cheqroom/hooks';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { handlePostAuthentication } from '../../api/authenticate.api';
import { useRecentWorkspaces } from '../../api/find-workspaces.api';
import { useDoFetchSsoTokens } from '../../api/get-tokens-for-sso-login.api';
import { Step } from '../../App';
import { useRedirectUri } from '../../hooks/use-redirect-uri.hook';

const getErrorMessage = (error: Error, t: TFunction) => {
	if (error.message.includes('403')) {
		return t('sso_redirect_step.no_access');
	}

	if (error.message.includes('402')) {
		return t('sso_redirect_step.subscription_expired');
	}

	return t('sso_redirect_step.sso_failed');
};

export const useLoginWithOAuthCode = () => {
	const [params] = useSearchParams();
	const navigateTo = useNavigate();
	const [isMobileApp] = useSessionStorage<boolean>('mobile');
	const [storedRedirectUri, clearRedirectUri] = useRedirectUri();
	const { add: addRecentWorkspace } = useRecentWorkspaces();
	const { t } = useTranslation();

	// Get the params from the url and use them to identify with the python API
	const code = params.get('code');
	const state = params.get('state');

	const ssoCallbackUrl = new URL(`${window.location.origin}${Step.SSO_REDIRECT}`);

	// Pass strings; query is disabled when code/state missing so API is never called with empty values
	return useDoFetchSsoTokens(
		{
			code: code ?? '',
			state: state ?? '',
			redirectUri: ssoCallbackUrl.toString(),
		},
		{
			enabled: !!code && !!state,
			onSuccess: ({ accessToken, refreshToken }) => {
				void handlePostAuthentication({
					accessToken,
					refreshToken,
					isMobileApp,
					redirectUri: storedRedirectUri,
					navigateTo,
					addRecentWorkspace,
					clearStorage: clearRedirectUri,
				});
			},
			onError: (error) => {
				return navigateTo(Step.SELECT_WORKSPACE, {
					state: {
						error: getErrorMessage(error, t),
					},
				});
			},
		}
	);
};
